import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X, Search, ChevronUp, ChevronDown, Trash2, Check, AlertCircle, Flame, Beef, Wheat, Droplet, AlertTriangle, ExternalLink, Tag, RefreshCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import { toFraction } from '../../utils/fractions';
import {
  provenanceOnSelectCoreFood,
  provenanceOnMacroEdit,
  provenanceFromStored,
} from '../../utils/macrosProvenance';
import {
  COOKWARE_OPTIONS, CUISINE_OPTIONS, PROTEIN_OPTIONS, DEFAULT_UNIQUENESS_GROUPS,
} from '../../constants/mealOptions';

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-card border border-border rounded-xl p-6 max-w-md mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-500/10 rounded-full">
            <AlertTriangle className="text-red-500" size={24} />
          </div>
          <h3 className="font-bold text-lg">Delete Meal?</h3>
        </div>
        <p className="text-muted-foreground mb-6">
          Are you sure you want to delete <strong>"{title}"</strong>? This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid="confirm-delete-btn">
            <Trash2 size={16} className="mr-1" />Delete
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// =============== INGREDIENT ROW COMPONENT ===============
const IngredientRow = ({ 
  ingredient, 
  index, 
  isFirst,
  isLast,
  onUpdate, 
  onRemove, 
  onMoveUp, 
  onMoveDown,
  onSearch,
  searchQuery,
  searchResults,
  isSearching,
  isActiveSearch,
  onSelectFood,
  onClearSearch,
  onToggleOptional
}) => {
  const hasFood = Boolean(ingredient.id);
  
  // GRAMS is the source of truth. Display fields derive from grams.
  
  const handleUnitChange = (unitId) => {
    const option = (ingredient.portionOptions || []).find(o => o.id === unitId);
    const gpdu = option ? option.grams_per_unit : 1;
    const grams = ingredient.grams || 0;
    // Derive display_qty from grams and the new unit's conversion factor
    const newQty = gpdu > 0 ? Math.round((grams / gpdu) * 100) / 100 : grams;
    onUpdate(index, { 
      displayUnitId: unitId, 
      gramsPerDisplayUnit: gpdu,
      displayQty: newQty
    });
  };
  
  const handleQtyChange = (qty) => {
    // Changing display qty derives grams_per_display_unit, grams stays stable
    const grams = ingredient.grams || 0;
    const newGpdu = qty > 0 ? Math.round((grams / qty) * 10000) / 10000 : 0;
    onUpdate(index, { 
      displayQty: qty, 
      gramsPerDisplayUnit: newGpdu
    });
  };
  
  const handleGramsChange = (grams) => {
    // Grams is directly edited — derive display_qty from gramsPerDisplayUnit
    const gpdu = ingredient.gramsPerDisplayUnit || 1;
    const newQty = gpdu > 0 ? Math.round((grams / gpdu) * 100) / 100 : grams;
    onUpdate(index, { 
      grams: grams, 
      displayQty: newQty
    });
  };
  
  const handleGramsPerDisplayUnitChange = (gpdu) => {
    // Changing g/display_unit derives display_qty, grams stays stable
    const grams = ingredient.grams || 0;
    const newQty = gpdu > 0 ? Math.round((grams / gpdu) * 100) / 100 : 0;
    onUpdate(index, {
      gramsPerDisplayUnit: gpdu,
      displayQty: newQty
    });
  };
  
  if (!hasFood) {
    // Search mode - show search input
    return (
      <div className="bg-card border border-border/40 rounded-xl p-3" data-testid={`ingredient-row-${index}`}>
        <div className="space-y-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input 
              value={isActiveSearch ? searchQuery : ''} 
              onChange={e => onSearch(index, e.target.value)} 
              onFocus={() => onSearch(index, searchQuery || '')} 
              placeholder="Search foods (e.g., chicken, rice, broccoli)..." 
              className="pl-9" 
              data-testid={`ingredient-search-${index}`}
            />
            {isSearching && isActiveSearch && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            )}
          </div>
          
          {isActiveSearch && searchResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto bg-secondary/20 rounded-lg p-2 space-y-1 border border-border/40">
              {searchResults.map(food => (
                <button 
                  key={food.food_id} 
                  onClick={() => onSelectFood(index, food)} 
                  className="w-full p-2 text-left text-sm hover:bg-accent/10 rounded-lg transition-colors"
                  data-testid={`food-result-${food.food_id}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{food.name}</span>
                    <span className="text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded">
                      {Math.round(food.macros_per_100g.calories)} cal/100g
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {Math.round(food.macros_per_100g.protein)}g P • {Math.round(food.macros_per_100g.carbs)}g C • {Math.round(food.macros_per_100g.fat)}g F per 100g
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {isActiveSearch && searchQuery && searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">No foods found for "{searchQuery}"</p>
          )}
          
          <button 
            onClick={() => onRemove(index)} 
            className="text-xs text-red-500 hover:underline"
            data-testid={`remove-ingredient-${index}`}
          >
            Remove
          </button>
        </div>
      </div>
    );
  }
  
  // Selected food mode - show ingredient details
  return (
    <div className="bg-card border border-border/40 rounded-xl p-3" data-testid={`ingredient-row-${index}`}>
      <div className="space-y-3">
        {/* Header with name and actions */}
        <div className="flex justify-between">
          <div>
            <p className="font-medium text-sm">{ingredient.name}</p>
            <p className="text-xs text-muted-foreground">
              {Math.round(ingredient.macros?.calories || 0)} cal • {Math.round(ingredient.macros?.protein || 0)}g P • {Math.round(ingredient.macros?.carbs || 0)}g C • {Math.round(ingredient.macros?.fat || 0)}g F
            </p>
            {/* Read-only inherited Foods Core metadata. Correct these on the
                Core Food record itself (Admin -> Core Foods), never per-meal. */}
            {(ingredient.groceryCategory != null || ingredient.allowScaling != null) && (
              <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`ingredient-core-meta-${index}`}>
                {ingredient.groceryCategory != null && (
                  <span>Grocery: <span className="font-medium text-foreground">{ingredient.groceryCategory}</span></span>
                )}
                {ingredient.groceryCategory != null && ingredient.allowScaling != null && <span> · </span>}
                {ingredient.allowScaling != null && (
                  <span>Scaling: <span className={`font-medium ${ingredient.allowScaling ? 'text-emerald-600' : 'text-red-500'}`}>
                    {ingredient.allowScaling ? 'Allowed' : 'Blocked'}
                  </span></span>
                )}
              </p>
            )}
          </div>
          <div className="flex gap-1">
            <button 
              onClick={() => onMoveUp(index)} 
              disabled={isFirst} 
              className="p-1 disabled:opacity-30 hover:bg-secondary/50 rounded"
              title="Move up"
            >
              <ChevronUp size={16} />
            </button>
            <button 
              onClick={() => onMoveDown(index)} 
              disabled={isLast} 
              className="p-1 disabled:opacity-30 hover:bg-secondary/50 rounded"
              title="Move down"
            >
              <ChevronDown size={16} />
            </button>
            <button 
              onClick={() => onRemove(index)} 
              className="p-1 text-red-500 hover:bg-red-500/10 rounded"
              title="Remove"
              data-testid={`remove-ingredient-${index}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        
        {/* Optional toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleOptional(index)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
              ingredient.isOptional 
                ? 'bg-amber-500/20 text-amber-600 border border-amber-500/30' 
                : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50'
            }`}
            data-testid={`toggle-optional-${index}`}
          >
            <Tag size={12} />
            {ingredient.isOptional ? 'Optional' : 'Mark as Optional'}
          </button>
          {ingredient.isOptional && (
            <span className="text-xs text-muted-foreground">Won't affect macros, tagged in grocery list</span>
          )}
        </div>
        
        {/* Quantity and Unit selectors */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Qty</Label>
            <Input 
              type="number" 
              min="0.1" 
              step="0.25"
              value={ingredient.displayQty || ''} 
              onChange={e => handleQtyChange(parseFloat(e.target.value) || 0)} 
              className="h-9 mt-1" 
              placeholder="1"
              data-testid={`ingredient-qty-${index}`}
            />
          </div>
          <div>
            <Label className="text-xs">Unit</Label>
            <select 
              value={ingredient.displayUnitId || 'g'} 
              onChange={e => handleUnitChange(e.target.value)}
              className="w-full h-9 px-2 rounded-lg bg-secondary/30 mt-1 text-sm"
              data-testid={`ingredient-unit-${index}`}
            >
              {(ingredient.portionOptions || [{ id: 'g', label: 'grams' }]).map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}{opt.is_estimate ? ' (est.)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Grams *</Label>
            <Input 
              type="number" 
              min="1" 
              value={ingredient.grams || ''} 
              onChange={e => handleGramsChange(parseFloat(e.target.value) || 0)} 
              className="h-9 mt-1" 
              data-testid={`ingredient-grams-${index}`}
            />
          </div>
        </div>
        
        {/* Editable Macros per ingredient */}
        <div className="mt-2 p-2 bg-secondary/20 rounded-lg">
          <Label className="text-xs text-muted-foreground mb-1 block">Macros (per {ingredient.grams || 100}g)</Label>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-[10px] text-orange-500">Cal</Label>
              <Input 
                type="number" 
                min="0"
                value={ingredient.macros?.calories || ''} 
                onChange={e => {
                  const newCal = parseFloat(e.target.value) || 0;
                  const grams = ingredient.grams || 100;
                  onUpdate(index, { 
                    macros: { ...ingredient.macros, calories: newCal },
                    _perG: { 
                      ...ingredient._perG, 
                      cal: grams > 0 ? newCal / grams : 0 
                    }
                  });
                }} 
                className="h-8 mt-0.5 text-sm" 
                data-testid={`ingredient-cal-${index}`}
              />
            </div>
            <div>
              <Label className="text-[10px] text-red-500">Protein</Label>
              <Input 
                type="number" 
                min="0"
                step="0.1"
                value={ingredient.macros?.protein || ''} 
                onChange={e => {
                  const newPro = parseFloat(e.target.value) || 0;
                  const grams = ingredient.grams || 100;
                  onUpdate(index, { 
                    macros: { ...ingredient.macros, protein: newPro },
                    _perG: { 
                      ...ingredient._perG, 
                      protein: grams > 0 ? newPro / grams : 0 
                    }
                  });
                }} 
                className="h-8 mt-0.5 text-sm" 
                data-testid={`ingredient-protein-${index}`}
              />
            </div>
            <div>
              <Label className="text-[10px] text-amber-500">Carbs</Label>
              <Input 
                type="number" 
                min="0"
                step="0.1"
                value={ingredient.macros?.carbs || ''} 
                onChange={e => {
                  const newCarbs = parseFloat(e.target.value) || 0;
                  const grams = ingredient.grams || 100;
                  onUpdate(index, { 
                    macros: { ...ingredient.macros, carbs: newCarbs },
                    _perG: { 
                      ...ingredient._perG, 
                      carbs: grams > 0 ? newCarbs / grams : 0 
                    }
                  });
                }} 
                className="h-8 mt-0.5 text-sm" 
                data-testid={`ingredient-carbs-${index}`}
              />
            </div>
            <div>
              <Label className="text-[10px] text-blue-500">Fat</Label>
              <Input 
                type="number" 
                min="0"
                step="0.1"
                value={ingredient.macros?.fat || ''} 
                onChange={e => {
                  const newFat = parseFloat(e.target.value) || 0;
                  const grams = ingredient.grams || 100;
                  onUpdate(index, { 
                    macros: { ...ingredient.macros, fat: newFat },
                    _perG: { 
                      ...ingredient._perG, 
                      fat: grams > 0 ? newFat / grams : 0 
                    }
                  });
                }} 
                className="h-8 mt-0.5 text-sm" 
                data-testid={`ingredient-fat-${index}`}
              />
            </div>
          </div>
        </div>
        
        {/* Display Measurement (structured) + grams/unit conversion */}
        <div className="mt-2 p-2 bg-blue-500/5 border border-blue-500/10 rounded-lg">
          <Label className="text-xs text-blue-400 mb-1.5 block font-medium">Display Measurement</Label>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-muted-foreground">Display Qty</Label>
              <Input 
                type="number" 
                min="0.01" 
                step="0.25"
                value={ingredient.displayQty ?? ''} 
                onChange={e => {
                  const newQty = parseFloat(e.target.value) || 0;
                  const grams = ingredient.grams || 0;
                  // Grams stays stable — derive grams_per_display_unit
                  const newGpdu = newQty > 0 ? Math.round((grams / newQty) * 10000) / 10000 : 0;
                  onUpdate(index, { 
                    displayQty: newQty, 
                    gramsPerDisplayUnit: newGpdu
                  });
                }} 
                className="h-8 mt-0.5 text-sm" 
                data-testid={`ingredient-display-qty-${index}`}
              />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Display Unit</Label>
              <Input 
                value={ingredient.displayUnitId || ''} 
                onChange={e => onUpdate(index, { displayUnitId: e.target.value })} 
                className="h-8 mt-0.5 text-sm" 
                placeholder="cup, tbsp, oz..."
                data-testid={`ingredient-display-unit-${index}`}
              />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">g / display unit</Label>
              <Input 
                type="number" 
                min="0.01" 
                step="0.1"
                value={ingredient.gramsPerDisplayUnit ?? ''} 
                onChange={e => handleGramsPerDisplayUnitChange(parseFloat(e.target.value) || 0)} 
                className="h-8 mt-0.5 text-sm" 
                placeholder="28.35"
                data-testid={`ingredient-gpdu-${index}`}
              />
            </div>
          </div>
          {ingredient.displayQty && ingredient.gramsPerDisplayUnit && ingredient.displayUnitId && ingredient.displayUnitId !== 'g' && (
            <p className="text-[10px] text-muted-foreground mt-1.5">
              {toFraction(ingredient.displayQty)} {ingredient.displayUnitId} = {Math.round((ingredient.displayQty * ingredient.gramsPerDisplayUnit) * 10) / 10}g
            </p>
          )}
        </div>
        
        {/* Prep note and display name */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>
            <Label className="text-xs">Prep note</Label>
            <Input 
              value={ingredient.prep || ''} 
              onChange={e => onUpdate(index, { prep: e.target.value })} 
              className="h-9 mt-1" 
              placeholder="diced, chopped..."
            />
          </div>
          <div>
            <Label className="text-xs">Display Name</Label>
            <Input 
              value={ingredient.display || ''} 
              onChange={e => onUpdate(index, { display: e.target.value })} 
              className="h-9 mt-1" 
              placeholder="Ground Beef"
              data-testid={`ingredient-display-name-${index}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// =============== MAIN MEAL EDITOR COMPONENT ===============
const MealEditor = () => {
  const navigate = useNavigate();
  const params = useParams();
  const mealId = params.mealId;
  const isNew = !mealId || mealId === 'new';

  // Auth & loading states
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  
  // Meal data
  const [title, setTitle] = useState('');
  const [mealType, setMealType] = useState('lunch');
  const [servings, setServings] = useState(1);
  const [cookTime, setCookTime] = useState(0);
  const [imageUrl, setImageUrl] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [status, setStatus] = useState('draft');
  const [instructions, setInstructions] = useState(['']);
  const [errors, setErrors] = useState([]);
  
  // Tag management
  const [showTagModal, setShowTagModal] = useState(false);
  const [allExistingTags, setAllExistingTags] = useState([]);
  
  // Cooking preferences
  const [requiredCookware, setRequiredCookware] = useState([]);
  const [difficulty, setDifficulty] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [proteinType, setProteinType] = useState('');  
  // Meal-level scaling behavior override (admin escape hatch). '' = Inherit
  // from ingredients (backend null); 'full' | 'limited' | 'none' map 1:1 to
  // the backend scaling_tier_override.
  const [scalingBehavior, setScalingBehavior] = useState('');
  // Meal group
  const [groupId, setGroupId] = useState('');
  const [availableGroups, setAvailableGroups] = useState([]);
  
  // Uniqueness Group (for repeat rules)
  const [uniquenessGroup, setUniquenessGroup] = useState('');
  const [availableUniquenessGroups, setAvailableUniquenessGroups] = useState([]);
  
  // Default uniqueness groups — re-exported via constants/mealOptions.js so
  // this page and the bulk-import review page stay in sync.
  
  // Macro override
  const [macroOverride, setMacroOverride] = useState(null);
  const [macroOverrideActive, setMacroOverrideActive] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Ingredients state - using a ref to track unique keys
  const ingredientKeyCounter = useRef(0);
  const [ingredients, setIngredients] = useState([]);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingFood, setSearchingFood] = useState(false);
  const [activeSearchIdx, setActiveSearchIdx] = useState(null);
  const searchTimeout = useRef(null);
  
  // Option lists — sourced from ../../constants/mealOptions.js above
  // (previously duplicated inline here and in AdminMealImport.jsx)

  // Check admin access
  useEffect(() => {
    adminAPI.checkAdmin()
      .then(res => { 
        if (res.data.is_admin) setIsAdmin(true);
        else { toast.error('Not authorized'); navigate('/home'); }
      })
      .catch(() => { toast.error('Not authorized'); navigate('/home'); })
      .finally(() => setCheckingAdmin(false));
  }, [navigate]);

  // Load meal groups
  useEffect(() => {
    if (!isAdmin) return;
    adminAPI.listMealGroups()
      .then(res => setAvailableGroups(res.data || []))
      .catch(err => console.error('Failed to load groups:', err));
  }, [isAdmin]);

  // Load uniqueness groups
  useEffect(() => {
    if (!isAdmin) return;
    adminAPI.listUniquenessGroups()
      .then(res => {
        const groups = res.data || [];
        // Extract just the group names
        const groupNames = groups.map(g => g.name || g);
        // Merge with defaults to ensure we have common options
        const merged = [...new Set([...DEFAULT_UNIQUENESS_GROUPS, ...groupNames])];
        merged.sort();
        setAvailableUniquenessGroups(merged);
      })
      .catch(err => {
        console.error('Failed to load uniqueness groups:', err);
        // Fallback to defaults
        setAvailableUniquenessGroups(DEFAULT_UNIQUENESS_GROUPS);
      });
  }, [isAdmin]);

  // Load all existing tags when modal opens
  useEffect(() => {
    if (!showTagModal || !isAdmin) return;
    adminAPI.listTags()
      .then(res => setAllExistingTags(res.data || []))
      .catch(err => console.error('Failed to load tags:', err));
  }, [showTagModal, isAdmin]);

  // Load meal data
  useEffect(() => {
    if (!isAdmin || isNew) return;
    adminAPI.getMeal(mealId)
      .then(res => {
        const m = res.data.meal;
        setTitle(m.title || '');
        setMealType(m.meal_type || 'lunch');
        setServings(m.servings || 1);
        setCookTime(m.cook_time_min || 0);
        setImageUrl(m.image_url || '');
        setTags(m.tags || []);
        setStatus(m.status || 'draft');
        setInstructions(m.instructions && m.instructions.length ? m.instructions : ['']);
        
        // Load cooking preferences
        setRequiredCookware(m.required_cookware || []);
        setDifficulty(m.difficulty || '');
        setCuisine(m.cuisine || '');
        setProteinType(m.protein_type || '');
        // Explicit meal-level scaling override ('' = Inherit)
        setScalingBehavior(m.scaling_tier_override || '');
        
        // Load group
        setGroupId(m.group_id || '');
        
        // Load Uniqueness Group
        setUniquenessGroup(m.uniqueness_group || '');
        
        // Load macro override
        if (m.macro_override) {
          setMacroOverride(m.macro_override);
          setMacroOverrideActive(res.data.macro_override_active || false);
        }
        
        // Load ingredients with unique keys
        const loadedIngredients = res.data.ingredients.map(i => {
          ingredientKeyCounter.current += 1;
          // Resolve gramsPerDisplayUnit: explicit > from portion option > computed from qty
          let gpdu = i.grams_per_display_unit || null;
          if (!gpdu && i.display_qty && i.display_qty > 0 && i.amount_grams) {
            gpdu = Math.round((i.amount_grams / i.display_qty) * 10000) / 10000;
          }
          return {
            key: `ing_${ingredientKeyCounter.current}`,
            id: i.core_food_id,
            name: i.food_name,
            grams: i.amount_grams,
            displayQty: i.display_qty || null,
            displayUnitId: i.display_unit_id || 'g',
            gramsPerDisplayUnit: gpdu,
            display: i.display_text || '',
            prep: i.prep_note || '',
            macros: i.macros || { calories: 0, protein: 0, carbs: 0, fat: 0 },
            _perG: i._perG || null,
            portionOptions: i.portion_options || [{ id: 'g', label: 'grams', grams_per_unit: 1 }],
            isOptional: i.is_optional || false,
            // Explicit macro provenance (preserved on load; legacy null stays
            // unknown and is never guessed).
            macrosSource: provenanceFromStored(i.macros_source),
            // Inherited Foods Core metadata (read-only display only)
            groceryCategory: i.resolved_grocery_category ?? null,
            allowScaling: (i.resolved_allow_meal_scaling === undefined ? null : i.resolved_allow_meal_scaling)
          };
        });
        setIngredients(loadedIngredients);
      })
      .catch((err) => { 
        console.error('Failed to load meal:', err);
        toast.error('Failed to load meal'); 
        navigate('/admin/meals'); 
      })
      .finally(() => setLoading(false));
  }, [isAdmin, isNew, mealId, navigate]);

  // Debounced food search
  const handleSearch = useCallback((idx, query) => {
    setActiveSearchIdx(idx);
    setSearchQuery(query);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    searchTimeout.current = setTimeout(() => {
      setSearchingFood(true);
      adminAPI.searchCoreFoods(query, 20)
        .then(res => {
          setSearchResults(res.data || []);
        })
        .catch(err => {
          console.error('Food search error:', err);
          setSearchResults([]);
        })
        .finally(() => setSearchingFood(false));
    }, 300);
  }, []);

  // Select food from search results
  const handleSelectFood = useCallback((idx, food) => {
    const defaultGrams = 100;
    const perG = {
      cal: food.cal_per_g || (food.macros_per_100g?.calories / 100) || 0,
      protein: food.protein_per_g || (food.macros_per_100g?.protein / 100) || 0,
      carbs: food.carbs_per_g || (food.macros_per_100g?.carbs / 100) || 0,
      fat: food.fat_per_g || (food.macros_per_100g?.fat / 100) || 0
    };
    
    const macros = { 
      calories: Math.round(perG.cal * defaultGrams), 
      protein: Math.round(perG.protein * defaultGrams * 10) / 10, 
      carbs: Math.round(perG.carbs * defaultGrams * 10) / 10, 
      fat: Math.round(perG.fat * defaultGrams * 10) / 10 
    };
    
    setIngredients(prev => {
      const updated = [...prev];
      updated[idx] = { 
        ...updated[idx], 
        id: food.food_id, 
        name: food.name, 
        grams: defaultGrams,
        displayQty: 100,
        displayUnitId: 'g',
        gramsPerDisplayUnit: 1,
        display: food.name, 
        macros: macros, 
        _perG: perG,
        // Selecting a Core Food => the recipe uses Foods Core nutrition.
        macrosSource: provenanceOnSelectCoreFood(),
        portionOptions: food.portion_options || [{ id: 'g', label: 'grams', grams_per_unit: 1 }]
      };
      return updated;
    });
    
    setSearchResults([]);
    setActiveSearchIdx(null);
    setSearchQuery('');
  }, []);

  // Update ingredient field
  const handleUpdateIngredient = useCallback((idx, updates) => {
    setIngredients(prev => {
      const updated = [...prev];
      const ing = { ...updated[idx], ...updates };
      
      // Editing any nutrition macro establishes recipe-specific provenance.
      // Quantity/grams/unit/prep/display changes carry no `_perG` and leave
      // provenance unchanged.
      if (Object.prototype.hasOwnProperty.call(updates, '_perG')) {
        ing.macrosSource = provenanceOnMacroEdit();
      }
      
      // Recalculate macros if grams changed
      if (updates.grams !== undefined && ing._perG) {
        const g = updates.grams;
        ing.macros = {
          calories: Math.round(ing._perG.cal * g),
          protein: Math.round(ing._perG.protein * g * 10) / 10,
          carbs: Math.round(ing._perG.carbs * g * 10) / 10,
          fat: Math.round(ing._perG.fat * g * 10) / 10
        };
      }
      
      updated[idx] = ing;
      return updated;
    });
  }, []);

  // Add new ingredient
  const handleAddIngredient = useCallback(() => {
    ingredientKeyCounter.current += 1;
    setIngredients(prev => [...prev, { 
      key: `ing_${ingredientKeyCounter.current}`,
      id: '', 
      name: '', 
      grams: 100, 
      displayQty: null,
      displayUnitId: 'g',
      gramsPerDisplayUnit: 1,
      display: '', 
      prep: '', 
      macros: { calories: 0, protein: 0, carbs: 0, fat: 0 }, 
      _perG: null,
      macrosSource: null,
      portionOptions: [{ id: 'g', label: 'grams', grams_per_unit: 1 }]
    }]);
  }, []);

  // Remove ingredient
  const handleRemoveIngredient = useCallback((idx) => {
    setIngredients(prev => prev.filter((_, i) => i !== idx));
    if (activeSearchIdx === idx) {
      setActiveSearchIdx(null);
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [activeSearchIdx]);

  // Move ingredient up
  const handleMoveUp = useCallback((idx) => {
    if (idx <= 0) return;
    setIngredients(prev => {
      const updated = [...prev];
      [updated[idx], updated[idx - 1]] = [updated[idx - 1], updated[idx]];
      return updated;
    });
  }, []);

  // Move ingredient down
  const handleMoveDown = useCallback((idx) => {
    setIngredients(prev => {
      if (idx >= prev.length - 1) return prev;
      const updated = [...prev];
      [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
      return updated;
    });
  }, []);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setActiveSearchIdx(null);
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  // Toggle optional status
  const handleToggleOptional = useCallback((idx) => {
    setIngredients(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], isOptional: !updated[idx].isOptional };
      return updated;
    });
  }, []);

  // Calculate computed macros
  const computedMacros = {
    total: { cal: 0, protein: 0, carbs: 0, fat: 0 },
    perServing: { cal: 0, protein: 0, carbs: 0, fat: 0 }
  };
  
  ingredients.forEach(ing => {
    if (ing.macros) {
      // Sum up macros from ingredients (protein, carbs, fat)
      computedMacros.total.protein += ing.macros.protein || 0;
      computedMacros.total.carbs += ing.macros.carbs || 0;
      computedMacros.total.fat += ing.macros.fat || 0;
    }
  });
  
  // Compute calories using 4/4/9 rule from summed macros
  computedMacros.total.cal = (computedMacros.total.protein * 4) + (computedMacros.total.carbs * 4) + (computedMacros.total.fat * 9);
  
  const s = servings || 1;
  const perServingProtein = Math.round(computedMacros.total.protein / s * 10) / 10;
  const perServingCarbs = Math.round(computedMacros.total.carbs / s * 10) / 10;
  const perServingFat = Math.round(computedMacros.total.fat / s * 10) / 10;
  
  computedMacros.perServing = {
    // Always compute calories from macros using 4/4/9 rule
    cal: Math.round((perServingProtein * 4) + (perServingCarbs * 4) + (perServingFat * 9)),
    protein: perServingProtein,
    carbs: perServingCarbs,
    fat: perServingFat
  };

  // Displayed macros (override or computed)
  const displayedMacros = macroOverrideActive && macroOverride ? {
    // Always compute calories from macros using 4/4/9 rule
    cal: ((macroOverride.protein || 0) * 4) + ((macroOverride.carbs || 0) * 4) + ((macroOverride.fat || 0) * 9),
    protein: macroOverride.protein || 0,
    carbs: macroOverride.carbs || 0,
    fat: macroOverride.fat || 0
  } : computedMacros.perServing;

  // Handle macro override toggle
  const handleToggleOverride = () => {
    if (macroOverrideActive) {
      // Disable override
      setMacroOverrideActive(false);
      setMacroOverride(null);
    } else {
      // Enable override with current computed values
      setMacroOverrideActive(true);
      setMacroOverride({
        calories: computedMacros.perServing.cal,
        protein: computedMacros.perServing.protein,
        carbs: computedMacros.perServing.carbs,
        fat: computedMacros.perServing.fat
      });
    }
  };

  // Save meal
  const handleSave = async (publish = false) => {
    const mealData = {
      meal: { 
        title, 
        meal_type: mealType, 
        servings, 
        cook_time_min: cookTime, 
        image_url: imageUrl, 
        tags, 
        status: publish ? 'published' : status, 
        instructions: instructions.filter(s => s.trim()),
        macro_override: macroOverrideActive ? macroOverride : null,
        required_cookware: requiredCookware,
        difficulty: difficulty || null,
        cuisine: cuisine || null,
        protein_type: proteinType || null,
        group_id: groupId || null,
        // Uniqueness Group (repeat rules are set at group level)
        uniqueness_group: uniquenessGroup || null,
        // Explicit meal-level scaling override. '' (Inherit) => null so the
        // derived/inherited restriction (or the normal engine) applies.
        scaling_tier_override: scalingBehavior || null
      },
      ingredients: ingredients
        .filter(i => i.id) // Only include ingredients with a selected food
        .map((i, idx) => ({ 
          core_food_id: i.id, 
          amount_grams: i.grams, 
          display_qty: i.displayQty,
          display_unit_id: i.displayUnitId,
          grams_per_display_unit: i.gramsPerDisplayUnit || null,
          display_text: i.display, 
          prep_note: i.prep, 
          sort_order: idx,
          is_optional: i.isOptional || false,
          // Include per-gram macros for USDA/external foods
          cal_per_g: i._perG?.cal || null,
          protein_per_g: i._perG?.protein || null,
          carbs_per_g: i._perG?.carbs || null,
          fat_per_g: i._perG?.fat || null,
          // Explicit provenance (never inferred server-side). null = unknown
          // legacy provenance preserved as-is.
          macros_source: i.macrosSource ?? null
        }))
    };
    
    // Validate only when explicitly publishing (not on regular saves of already-published meals)
    if (publish) {
      try {
        const v = await adminAPI.validateMeal(mealData);
        if (!v.data.valid) { 
          setErrors(v.data.errors || []); 
          toast.error('Fix validation errors before publishing'); 
          return; 
        }
      } catch (err) { 
        toast.error('Validation failed'); 
        return; 
      }
    }
    
    setSaving(true);
    try {
      if (isNew) { 
        const r = await adminAPI.createMeal(mealData); 
        toast.success('Meal created'); 
        navigate('/admin/meals/' + r.data.meal_id + '/edit'); 
      } else { 
        await adminAPI.updateMeal(mealId, mealData); 
        toast.success(publish ? 'Meal published' : 'Meal saved'); 
        if (publish) setStatus('published'); 
      }
      setErrors([]);
    } catch (err) { 
      console.error('Save error:', err);
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to save meal'); 
    }
    setSaving(false);
  };

  // Handle delete meal
  const handleDelete = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!mealId || isNew) return;
    
    // Show the delete confirmation modal
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    
    try {
      await adminAPI.deleteMeal(mealId);
      toast.success('Meal deleted');
      navigate('/admin/meals');
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete meal');
    }
  };

  // Loading states
  if (checkingAdmin || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal 
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title={title}
      />
      
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background border-b border-border/40 p-4 flex items-center justify-between header-safe-top">
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate('/admin/meals')} variant="ghost" size="sm">
            <ArrowLeft size={20} />
          </Button>
          <h1 className="font-bold text-lg">{isNew ? 'Create Meal' : 'Edit Meal'}</h1>
          {status === 'published' && (
            <span className="px-2 py-0.5 bg-green-500/20 text-green-500 text-xs rounded-full">Published</span>
          )}
        </div>
        <div className="flex gap-2">
          {!isNew && mealId && (
            <Button 
              onClick={() => window.open(`/meal-view/${mealId}`, '_blank')} 
              variant="outline" 
              size="sm"
              className="text-green-500 hover:bg-green-500/10 border-green-500/30"
              data-testid="preview-meal-btn"
            >
              <ExternalLink size={16} className="mr-1" />Preview
            </Button>
          )}
          {!isNew && (
            <Button 
              type="button"
              onClick={(e) => handleDelete(e)} 
              variant="outline" 
              size="sm"
              className="text-red-500 hover:bg-red-500/10 border-red-500/30"
              data-testid="delete-meal-btn"
            >
              <Trash2 size={16} className="mr-1" />Delete
            </Button>
          )}
          {status === 'draft' && (
            <Button 
              onClick={() => handleSave(true)} 
              disabled={saving} 
              variant="outline" 
              size="sm" 
              data-testid="publish-btn"
            >
              <Check size={16} className="mr-1" />Publish
            </Button>
          )}
          <Button 
            onClick={() => handleSave(false)} 
            disabled={saving} 
            size="sm" 
            className="bg-accent text-white" 
            data-testid="save-btn"
          >
            <Save size={16} className="mr-1" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Validation Errors */}
      {errors.length > 0 && (
        <div className="mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <AlertCircle size={18} className="text-red-500 inline mr-2" />
          <span className="text-red-600 text-sm">{errors.join(', ')}</span>
        </div>
      )}

      {/* Macros Preview */}
      <div className="mx-4 mt-4 p-4 bg-card border border-border/40 rounded-xl">
        <div className="flex justify-between items-center mb-2">
          <p className="text-xs text-muted-foreground">Macros per serving</p>
          {macroOverrideActive && (
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-600 text-xs rounded-full flex items-center gap-1">
              <AlertTriangle size={12} /> Override Active
            </span>
          )}
        </div>
        <div className="grid grid-cols-4 gap-3 text-center">
          <div>
            <Flame size={14} className="mx-auto text-orange-500" />
            <p className="font-bold">{displayedMacros.cal}</p>
            <p className="text-xs text-muted-foreground">Cal</p>
          </div>
          <div>
            <Beef size={14} className="mx-auto text-red-500" />
            <p className="font-bold">{displayedMacros.protein}g</p>
            <p className="text-xs text-muted-foreground">Protein</p>
          </div>
          <div>
            <Wheat size={14} className="mx-auto text-amber-500" />
            <p className="font-bold">{displayedMacros.carbs}g</p>
            <p className="text-xs text-muted-foreground">Carbs</p>
          </div>
          <div>
            <Droplet size={14} className="mx-auto text-blue-500" />
            <p className="font-bold">{displayedMacros.fat}g</p>
            <p className="text-xs text-muted-foreground">Fat</p>
          </div>
        </div>
        {macroOverrideActive && (
          <p className="text-xs text-center text-muted-foreground mt-2">
            Computed: {computedMacros.perServing.cal} cal • {computedMacros.perServing.protein}g P • {computedMacros.perServing.carbs}g C • {computedMacros.perServing.fat}g F
          </p>
        )}
      </div>

      <div className="p-4 space-y-6">
        {/* Meal Info Section */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase">Meal Info</h2>
          
          <div>
            <Label>Title *</Label>
            <Input 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              className="mt-1" 
              data-testid="meal-title" 
              placeholder="e.g., Grilled Chicken Salad"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <select 
                value={mealType} 
                onChange={e => setMealType(e.target.value)} 
                className="w-full h-10 px-3 rounded-lg bg-secondary/30 mt-1" 
                data-testid="meal-type"
              >
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            <div>
              <Label>Servings</Label>
              <Input 
                type="number" 
                min="1" 
                value={servings} 
                onChange={e => setServings(parseInt(e.target.value) || 1)} 
                className="mt-1" 
                data-testid="meal-servings" 
              />
            </div>
          </div>
          
          <div>
            <Label>Cook Time</Label>
            <p className="text-xs text-muted-foreground mb-2">Select total time including prep</p>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 15, label: '15 min' },
                { value: 30, label: '30 min' },
                { value: 45, label: '45 min' },
                { value: 60, label: '60+ min' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCookTime(opt.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    cookTime === opt.value
                      ? 'bg-accent text-white'
                      : 'bg-secondary/30 hover:bg-secondary/50'
                  }`}
                  data-testid={`cook-time-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Label htmlFor="cook-time-exact" className="text-xs text-muted-foreground whitespace-nowrap">Exact minutes</Label>
              <Input
                id="cook-time-exact"
                type="number"
                min={0}
                value={cookTime}
                onChange={e => {
                  const v = e.target.value;
                  setCookTime(v === '' ? 0 : Math.max(0, parseInt(v, 10) || 0));
                }}
                className="w-24 h-9"
                data-testid="cook-time-exact"
              />
              <span className="text-xs text-muted-foreground">min</span>
            </div>
          </div>
          
          <div>
            <Label>Image URL</Label>
            <Input 
              value={imageUrl} 
              onChange={e => setImageUrl(e.target.value)} 
              className="mt-1" 
              placeholder="https://..."
            />
            {imageUrl && (
              <img src={imageUrl} alt="" className="mt-2 w-20 h-20 rounded object-cover" />
            )}
          </div>
          
          <div>
            <Label>Meal Group</Label>
            <select 
              value={groupId} 
              onChange={e => setGroupId(e.target.value)} 
              className="w-full h-10 px-3 rounded-lg bg-secondary/30 mt-1" 
              data-testid="meal-group"
            >
              <option value="">No Group</option>
              {availableGroups.map(g => (
                <option key={g.group_id} value={g.group_id}>{g.name}</option>
              ))}
            </select>
          </div>
          
          {/* Uniqueness Group Section */}
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCcw size={14} className="text-blue-400" />
              <Label className="text-sm text-blue-300">Uniqueness Group</Label>
            </div>
            <div className="relative">
              <input
                list="uniqueness-group-options"
                value={uniquenessGroup}
                onChange={e => setUniquenessGroup(e.target.value)}
                placeholder="e.g., Smoothies, Salads, Wraps..."
                className="w-full h-10 px-3 rounded-lg bg-secondary/30 text-sm border border-blue-500/20"
                data-testid="uniqueness-group"
              />
              <datalist id="uniqueness-group-options">
                {availableUniquenessGroups.map(g => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </div>
            <p className="text-xs text-secondary-foreground/50 mt-1">
              Group similar meals together. Repeat rules are configured in the <span className="text-blue-400">Repeat Rules Overview</span>.
            </p>
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Tags</Label>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowTagModal(true)}
                className="h-7 text-xs"
              >
                <Tag size={14} className="mr-1" />
                Browse Tags
              </Button>
            </div>
            <div className="flex gap-2">
              <Input 
                value={tagInput} 
                onChange={e => setTagInput(e.target.value)} 
                onKeyPress={e => { 
                  if (e.key === 'Enter') { 
                    e.preventDefault(); 
                    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
                      setTags([...tags, tagInput.trim()]); 
                    }
                    setTagInput(''); 
                  }
                }} 
                placeholder="Add tag and press Enter"
              />
              <Button 
                onClick={() => { 
                  if (tagInput.trim() && !tags.includes(tagInput.trim())) {
                    setTags([...tags, tagInput.trim()]); 
                  }
                  setTagInput(''); 
                }} 
                variant="outline"
              >
                <Plus size={16} />
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map(t => (
                  <span 
                    key={t} 
                    className="px-2 py-1 bg-accent/10 text-accent rounded-full text-xs flex items-center gap-1"
                  >
                    {t}
                    <button onClick={() => setTags(tags.filter(x => x !== t))}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
            <span>Status: {status}</span>
            <button 
              onClick={() => setStatus(status === 'draft' ? 'published' : 'draft')} 
              className={`px-3 py-1 rounded-full text-sm ${status === 'published' ? 'bg-green-500 text-white' : 'bg-gray-500/20'}`} 
              data-testid="status-toggle"
            >
              {status}
            </button>
          </div>
        </section>

        {/* Cooking Preferences Section */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase">Cooking Preferences</h2>
          
          <div>
            <Label>Required Cookware</Label>
            <p className="text-xs text-muted-foreground mb-2">Select all appliances needed for this recipe</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {COOKWARE_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => {
                    if (requiredCookware.includes(opt.id)) {
                      setRequiredCookware(requiredCookware.filter(c => c !== opt.id));
                    } else {
                      setRequiredCookware([...requiredCookware, opt.id]);
                    }
                  }}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    requiredCookware.includes(opt.id) 
                      ? 'bg-accent text-white' 
                      : 'bg-secondary/30 hover:bg-secondary/50'
                  }`}
                  data-testid={`cookware-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <Label>Difficulty</Label>
            <select
              value={difficulty}
              onChange={e => setDifficulty(e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-secondary/30 mt-1"
              data-testid="difficulty"
            >
              <option value="">Not specified</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          
          <div>
            <Label>Cuisine</Label>
            <select
              value={cuisine}
              onChange={e => setCuisine(e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-secondary/30 mt-1"
              data-testid="cuisine"
            >
              <option value="">Not specified</option>
              {CUISINE_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <Label>Protein / Meat Type</Label>
            <select
              value={proteinType}
              onChange={e => setProteinType(e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-secondary/30 mt-1"
              data-testid="protein-type"
            >
              <option value="">Not specified</option>
              {PROTEIN_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label>Scaling behavior</Label>
            <select
              value={scalingBehavior}
              onChange={e => setScalingBehavior(e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-secondary/30 mt-1"
              data-testid="scaling-behavior"
            >
              <option value="">Inherit from ingredients</option>
              <option value="full">Full</option>
              <option value="limited">Limited</option>
              <option value="none">No scaling</option>
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Admin escape hatch for unusual recipes. "Inherit" uses the ingredients'
              Foods Core rules and the normal scaling engine. Setting a value here always
              wins; the engine still validates which portions are actually offered.
            </p>
          </div>
        </section>

        {/* Macro Override Section */}
        <section className="space-y-3">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase">Macro Override</h2>
            <Button 
              onClick={handleToggleOverride} 
              variant={macroOverrideActive ? "default" : "outline"} 
              size="sm"
              className={macroOverrideActive ? "bg-amber-500 hover:bg-amber-600" : ""}
              data-testid="toggle-override"
            >
              {macroOverrideActive ? "Override ON" : "Enable Override"}
            </Button>
          </div>
          
          {macroOverrideActive && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3">
              <p className="text-xs text-amber-600">
                <AlertTriangle size={14} className="inline mr-1" />
                Calories are auto-calculated using the 4/4/9 rule: (Protein × 4) + (Carbs × 4) + (Fat × 9)
              </p>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs">Calories</Label>
                  <Input 
                    type="number" 
                    value={((macroOverride?.protein || 0) * 4) + ((macroOverride?.carbs || 0) * 4) + ((macroOverride?.fat || 0) * 9)}
                    readOnly
                    disabled
                    className="h-9 mt-1 bg-muted cursor-not-allowed"
                    data-testid="override-calories"
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Auto-calculated</p>
                </div>
                <div>
                  <Label className="text-xs">Protein (g)</Label>
                  <Input 
                    type="number" 
                    min="0"
                    step="0.1"
                    value={macroOverride?.protein || ''} 
                    onChange={e => {
                      const protein = parseFloat(e.target.value) || 0;
                      setMacroOverride(prev => ({ 
                        ...prev, 
                        protein,
                        calories: (protein * 4) + ((prev?.carbs || 0) * 4) + ((prev?.fat || 0) * 9)
                      }));
                    }} 
                    className="h-9 mt-1"
                    data-testid="override-protein"
                  />
                </div>
                <div>
                  <Label className="text-xs">Carbs (g)</Label>
                  <Input 
                    type="number" 
                    min="0"
                    step="0.1"
                    value={macroOverride?.carbs || ''} 
                    onChange={e => {
                      const carbs = parseFloat(e.target.value) || 0;
                      setMacroOverride(prev => ({ 
                        ...prev, 
                        carbs,
                        calories: ((prev?.protein || 0) * 4) + (carbs * 4) + ((prev?.fat || 0) * 9)
                      }));
                    }} 
                    className="h-9 mt-1"
                    data-testid="override-carbs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Fat (g)</Label>
                  <Input 
                    type="number" 
                    min="0"
                    step="0.1"
                    value={macroOverride?.fat || ''} 
                    onChange={e => {
                      const fat = parseFloat(e.target.value) || 0;
                      setMacroOverride(prev => ({ 
                        ...prev, 
                        fat,
                        calories: ((prev?.protein || 0) * 4) + ((prev?.carbs || 0) * 4) + (fat * 9)
                      }));
                    }} 
                    className="h-9 mt-1"
                    data-testid="override-fat"
                  />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Ingredients Section */}
        <section className="space-y-3">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase">
              Ingredients ({ingredients.filter(i => i.id).length})
            </h2>
            <Button 
              onClick={handleAddIngredient} 
              variant="outline" 
              size="sm" 
              data-testid="add-ingredient"
            >
              <Plus size={16} className="mr-1" /> Add
            </Button>
          </div>
          
          {ingredients.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No ingredients yet. Click "Add" to search and add ingredients.
            </p>
          )}
          
          {ingredients.map((ing, idx) => (
            <IngredientRow
              key={ing.key}
              ingredient={ing}
              index={idx}
              isFirst={idx === 0}
              isLast={idx === ingredients.length - 1}
              onUpdate={handleUpdateIngredient}
              onRemove={handleRemoveIngredient}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onSearch={handleSearch}
              searchQuery={searchQuery}
              searchResults={searchResults}
              isSearching={searchingFood}
              isActiveSearch={activeSearchIdx === idx}
              onSelectFood={handleSelectFood}
              onClearSearch={handleClearSearch}
              onToggleOptional={handleToggleOptional}
            />
          ))}
        </section>

        {/* Instructions Section */}
        <section className="space-y-3">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase">
              Instructions ({instructions.filter(s => s.trim()).length})
            </h2>
            <Button 
              onClick={() => setInstructions([...instructions, ''])} 
              variant="outline" 
              size="sm" 
              data-testid="add-instruction"
            >
              <Plus size={16} className="mr-1" /> Add
            </Button>
          </div>
          
          {instructions.map((step, idx) => (
            <div key={idx} className="bg-card border border-border/40 rounded-xl p-3 flex gap-3">
              <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-accent">{idx + 1}</span>
              </div>
              <textarea 
                value={step} 
                onChange={e => { 
                  const u = [...instructions]; 
                  u[idx] = e.target.value; 
                  setInstructions(u); 
                }} 
                rows={2} 
                className="flex-1 px-3 py-2 rounded bg-secondary/30 resize-none text-sm" 
                placeholder="Describe this step..."
              />
              <div className="flex flex-col gap-1">
                <button 
                  onClick={() => { 
                    if (idx > 0) { 
                      const u = [...instructions]; 
                      [u[idx], u[idx-1]] = [u[idx-1], u[idx]]; 
                      setInstructions(u); 
                    }
                  }} 
                  disabled={idx === 0} 
                  className="p-1 disabled:opacity-30 hover:bg-secondary/50 rounded"
                >
                  <ChevronUp size={16} />
                </button>
                <button 
                  onClick={() => { 
                    if (idx < instructions.length - 1) { 
                      const u = [...instructions]; 
                      [u[idx], u[idx+1]] = [u[idx+1], u[idx]]; 
                      setInstructions(u); 
                    }
                  }} 
                  disabled={idx === instructions.length - 1} 
                  className="p-1 disabled:opacity-30 hover:bg-secondary/50 rounded"
                >
                  <ChevronDown size={16} />
                </button>
                <button 
                  onClick={() => { 
                    if (instructions.length > 1) {
                      setInstructions(instructions.filter((_, i) => i !== idx)); 
                    }
                  }} 
                  disabled={instructions.length <= 1} 
                  className="p-1 text-red-500 disabled:opacity-30 hover:bg-red-500/10 rounded"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </section>
      </div>

      {/* Tag Browser Modal */}
      <AnimatePresence>
        {showTagModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowTagModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4">Browse Tags</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Click a tag to add it, or create a new one below.
              </p>
              
              {/* Existing tags */}
              <div className="flex-1 overflow-y-auto mb-4">
                {allExistingTags.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    No existing tags found. Create one below!
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allExistingTags.map(t => {
                      const isSelected = tags.includes(t);
                      return (
                        <button
                          key={t}
                          onClick={() => {
                            if (isSelected) {
                              setTags(tags.filter(x => x !== t));
                            } else {
                              setTags([...tags, t]);
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                            isSelected 
                              ? 'bg-accent text-white' 
                              : 'bg-secondary/50 hover:bg-secondary text-foreground'
                          }`}
                        >
                          {isSelected && <Check size={14} className="inline mr-1" />}
                          {t}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Add new tag */}
              <div className="flex gap-2 border-t border-border/40 pt-4">
                <Input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  placeholder="New tag name..."
                  onKeyPress={e => {
                    if (e.key === 'Enter' && tagInput.trim()) {
                      e.preventDefault();
                      if (!tags.includes(tagInput.trim())) {
                        setTags([...tags, tagInput.trim()]);
                      }
                      if (!allExistingTags.includes(tagInput.trim())) {
                        setAllExistingTags([...allExistingTags, tagInput.trim()]);
                      }
                      setTagInput('');
                    }
                  }}
                />
                <Button 
                  onClick={() => {
                    if (tagInput.trim()) {
                      if (!tags.includes(tagInput.trim())) {
                        setTags([...tags, tagInput.trim()]);
                      }
                      if (!allExistingTags.includes(tagInput.trim())) {
                        setAllExistingTags([...allExistingTags, tagInput.trim()]);
                      }
                      setTagInput('');
                    }
                  }}
                  className="bg-accent"
                >
                  <Plus size={16} />
                </Button>
              </div>
              
              <Button variant="outline" className="w-full mt-4" onClick={() => setShowTagModal(false)}>
                Done
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MealEditor;
